import { useState, useEffect } from 'react';
import { RATINGS, api } from './_barrel.js';


        // =======================================================================
        // Subject detail view (with incidents and assessment)
        // =======================================================================
        // ===== Watch Status Strip =====
        // Live tiles at the top of the dossier showing the analyst's
        // current situational awareness for this subject. Replaces the
        // need to scroll five panels to read the headline picture.
        function WatchStatusStrip({ incidents, assessments, subject, token, subjectId, rubric }) {
            const [reports, setReports] = useState(null);

            useEffect(() => {
                (async () => {
                    try {
                        const r = await api(`/api/v1/subjects/${subjectId}/reports`, {}, token);
                        setReports(r);
                    } catch (e) { /* tile just won't show */ }
                })();
            }, [token, subjectId]);

            // Calculate the picture
            const allInc = incidents?.items || [];
            const activeInc = allInc.filter(i => !['closed','false_positive'].includes(i.status));

            const hostileActive = activeInc.filter(i =>
                ['threat','doxxing','impersonation','disinformation'].includes(i.incident_type_code) ||
                (i.llm_classification && i.llm_classification.startsWith('hostile_'))
            );
            const criticalActive = activeInc.filter(i => i.severity === 'critical');
            const highActive = activeInc.filter(i => i.severity === 'high');

            // Recent activity — last 30 days
            const cutoff = new Date();
            cutoff.setDate(cutoff.getDate() - 30);
            const recentInc = allInc.filter(i => new Date(i.detected_at) >= cutoff);
            const recentHostile = recentInc.filter(i =>
                (i.llm_classification && i.llm_classification.startsWith('hostile_')) ||
                ['threat','doxxing','impersonation','disinformation'].includes(i.incident_type_code)
            );

            // Last assessment date
            const activeAssessment = assessments?.items?.find(a => !a.superseded_at);

            // Authoritative current risk = the rubric score the analyst signs on
            // the Risk Score panel (rubric.composite is the effective value —
            // override if set, else the computed signals score). Falls back to the
            // legacy assessment only when no rubric exists yet. This keeps the tile,
            // the panel and the report all reading the same number.
            const riskComposite = (rubric != null && rubric.composite != null)
                ? Number(rubric.composite)
                : (activeAssessment ? Number(activeAssessment.composite_score) : null);
            const riskSigned = rubric != null ? !!rubric.signed_at : (activeAssessment != null);
            const riskDate = rubric != null ? (rubric.signed_at || rubric.generated_at)
                : (activeAssessment ? activeAssessment.created_at : null);
            const lastAssessmentDays = riskDate
                ? Math.floor((Date.now() - new Date(riskDate)) / (1000*60*60*24))
                : null;

            // Last signed report
            const signedReports = (reports || []).filter(r => r.signed_off_at);
            const lastSigned = signedReports.length > 0 ? signedReports[0] : null;
            const daysSinceSignoff = lastSigned
                ? Math.floor((Date.now() - new Date(lastSigned.signed_off_at)) / (1000*60*60*24))
                : null;

            // Risk band colour — canonical (see RATINGS)
            const bandColour = riskComposite != null ? RATINGS.hex(riskComposite) : 'var(--muted)';

            // Helper for tile rendering
            const Tile = ({ label, value, sub, valueColor, sublink, onSubClick }) => (
                <div className="panel p-5" style={{minHeight:'100px'}}>
                    <div className="font-mono text-xs uppercase tracking-widest text-muted mb-2">{label}</div>
                    <div className="font-display text-3xl" style={{color: valueColor || 'var(--paper)', lineHeight: 1.1}}>
                        {value}
                    </div>
                    {sub && (
                        sublink ? (
                            <div className="font-mono text-xs text-muted mt-2 cursor-pointer hover:text-[var(--gold)]" onClick={onSubClick}>{sub}</div>
                        ) : (
                            <div className="font-mono text-xs text-muted mt-2">{sub}</div>
                        )
                    )}
                </div>
            );

            return (
                <div className="grid grid-cols-4 gap-4 mb-8">
                    {/* Risk band tile */}
                    <Tile
                        label="Current Risk Band"
                        value={riskComposite != null ? RATINGS.label(riskComposite).toUpperCase() : 'NOT SET'}
                        sub={riskComposite != null
                            ? <span>score {riskComposite.toFixed(1)} · {riskSigned ? 'signed' : 'unsigned'}{lastAssessmentDays != null && lastAssessmentDays > 30 && <span style={{color:'var(--amber)', fontWeight:700}}> · REFRESH DUE</span>}</span>
                            : <span style={{color:'var(--amber)', fontWeight:700}}>no assessment — run one</span>}
                        valueColor={bandColour}
                    />
                    {/* Active hostile tile */}
                    <Tile
                        label="Active Hostile"
                        value={hostileActive.length}
                        sub={
                            criticalActive.length + highActive.length > 0
                                ? `${criticalActive.length} critical · ${highActive.length} high`
                                : 'no critical or high'
                        }
                        valueColor={hostileActive.length > 0 ? 'var(--gold)' : 'var(--muted)'}
                    />
                    {/* Recent 30 days tile */}
                    <Tile
                        label="Last 30 Days"
                        value={`+${recentInc.length}`}
                        sub={
                            recentHostile.length > 0
                                ? `${recentHostile.length} hostile detected`
                                : `${recentInc.length} new mentions, none hostile`
                        }
                        valueColor={recentHostile.length > 0 ? 'var(--amber)' : 'var(--paper)'}
                    />
                    {/* Sign-off freshness tile */}
                    <Tile
                        label="Last Signed Report"
                        value={lastSigned ? (daysSinceSignoff === 0 ? 'TODAY' : `${daysSinceSignoff}d`) : 'NONE'}
                        sub={lastSigned ? lastSigned.report_number : 'never signed off'}
                        valueColor={lastSigned ? (daysSinceSignoff <= 30 ? 'var(--gold)' : 'var(--amber)') : 'var(--muted)'}
                    />
                </div>
            );
        }

export { WatchStatusStrip };
